import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { formatMoney } from '@bookingdei/i18n/format-money';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { api } from '../api';
import { useLanguage } from './language-context';
import { usePlan } from './plan-context';
import { useSettings } from './settings-context';
import { useResource } from './use-resource';
import type { Plan, PlanEntitlements } from '../mock/seeds/schema-types';
import './plan-gate.css';

export interface PreviewCard {
  label: string;
  value: string;
}

/**
 * The cheapest active plan that includes an entitlement, and its lowest term price —
 * the locked page's "Needs: Premium · from ৳149" (design lockTierLine / priceFrom).
 * Plan data from GET /pricing, never a tier name or price literal (INF-1, DR-31).
 */
export function unlockingPlan(
  plans: readonly Plan[],
  entitlement: keyof PlanEntitlements,
): { plan: Plan; fromMinor: number | null } | null {
  const grants = (p: Plan) => {
    const v = p.entitlements?.[entitlement];
    return v === true || (typeof v === 'number' && v > 0) || v === null;
  };
  const plan = [...plans]
    .filter((p) => p.active !== false && grants(p))
    .sort((a, b) => (a.tier_order ?? 0) - (b.tier_order ?? 0))[0];
  if (!plan) return null;
  let fromMinor: number | null = null;
  for (const price of plan.prices ?? []) {
    const v = price.launch_minor ?? price.regular_minor;
    if (typeof v === 'number' && (fromMinor === null || v < fromMinor)) fromMinor = v;
  }
  return { plan, fromMinor };
}

// The locked page (design M:3806-3838, FR-B22): the unlocking tier as a tag, the
// "Upgrade to unlock" card with what the page does, the tier and its starting price,
// Upgrade → My Plan, and a blurred, labelled preview of the page. The preview figures
// are the page's illustration (the export's own sample numbers), hidden from assistive
// tech — never the merchant's data, which the server withholds on this plan (403).
export function LockedPage({
  entitlement,
  pageKey,
  benefitKey,
  preview,
}: {
  entitlement: keyof PlanEntitlements;
  pageKey: MessageKey;
  benefitKey: MessageKey;
  preview: readonly PreviewCard[];
}) {
  const { lang, t } = useLanguage();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const pricing = useResource<Plan[]>(
    async (signal) => {
      try {
        return ((await api.GET('/pricing', { signal, lang })).plans ?? []) as Plan[];
      } catch {
        return [];
      }
    },
    [lang],
  );
  const unlock = unlockingPlan(pricing.data ?? [], entitlement);
  const tier = unlock ? (lang === 'bn' && unlock.plan.name_bn ? unlock.plan.name_bn : unlock.plan.name_en) : null;
  const price =
    unlock && unlock.fromMinor !== null
      ? formatMoney(lang, unlock.fromMinor, {
          currencySymbol: settings?.currency.symbol ?? settings?.currency.code ?? '',
          minorUnitScale: settings?.currency.minor_unit_scale ?? 100,
        })
      : null;

  return (
    <section className="lock-page" aria-labelledby="lock-page-title" data-locked={entitlement}>
      <h1 id="lock-page-title" className="visually-hidden">
        {formatMessage(lang, 'dc70.lock.title', { page: t(pageKey) })}
      </h1>
      <div className="lock-page__tags">{tier && <span className="lock-page__tag">{tier}</span>}</div>
      <div className="lock-page__card">
        <span className="lock-page__kicker">{t('dc70.lock.kicker')}</span>
        <p className="lock-page__benefit">{t(benefitKey)}</p>
        {tier && (
          <div className="lock-page__tier">
            <span>{formatMessage(lang, 'dc70.lock.needs', { tier })}</span>
            {price && <span className="lock-page__price">{formatMessage(lang, 'dc70.lock.from', { price })}</span>}
          </div>
        )}
        <button type="button" className="lock-page__cta" onClick={() => navigate('/plan')}>
          {t('dc70.lock.upgrade')}
        </button>
      </div>
      <div className="lock-page__preview">
        <div className="lock-page__blur" aria-hidden="true">
          <div className="lock-page__cards">
            {preview.map((c) => (
              <div key={c.label} className="lock-page__pcard">
                <span className="lock-page__plabel">{c.label}</span>
                <span className="lock-page__pvalue">{c.value}</span>
              </div>
            ))}
          </div>
          {['78%', '92%', '64%', '84%'].map((w) => (
            <div key={w} className="lock-page__row" style={{ width: w }} />
          ))}
        </div>
        <span className="lock-page__label">{t('dc70.lock.preview')}</span>
      </div>
    </section>
  );
}

// Route-level plan gate (FR-B22). When the active plan's entitlement is false the route
// renders its locked page (`locked`, a LockedPage) instead of the screen: locks render,
// never hide, and never block silently. Unknown entitlements (loading, no row) show the
// screen; the server still enforces the entitlement on every read, and a screen answers
// that 403 with the same locked page.
export function PlanGate({
  entitlement,
  locked,
  children,
}: {
  entitlement: keyof PlanEntitlements;
  locked: ReactNode;
  children: ReactNode;
}) {
  const { entitlements } = usePlan();
  if (!entitlements || entitlements[entitlement] !== false) return <>{children}</>;
  return <>{locked}</>;
}
